import React, { useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useModal } from '@blueprint/ui';
import { useSavedListing } from 'listing-details/hooks';
import {
    ButtonSave,
    ButtonShare
} from 'listing-details/components/Buttons/Buttons';
import { UserActionsProps } from './UserActions.types';
import {
    StyledUserActions,
    StyledUserActionsGallery
} from './UserActions.styles';
import {
    trackRemoveSavedListing,
    trackSaveListing,
    trackSaveWhenNotSignedIn,
    trackOpenSharing
} from './UserActions.tracking';
import { BaseTaxonomy } from 'shared/tracking/contracts';
import { AuthModalMode } from 'shared/components/AuthModal/AuthModal.types';

const DynamicAuthPrompts = dynamic(
    () => import('shared/components/AuthPrompts/AuthPrompts'),
    { ssr: false }
);

const DynamicAuthModal = dynamic(
    () => import('shared/components/AuthModal/AuthModal'),
    { ssr: false }
);
const trackSaveButtonInteraction = (
    listingId: number,
    isUser: boolean | undefined,
    isSaved: boolean | undefined
) => {
    // Context hasn't been initialised yet so there's nothing to track
    if (isSaved === undefined) {
        return;
    }

    if (!isUser) {
        return trackSaveWhenNotSignedIn(listingId);
    }

    if (isSaved) {
        trackRemoveSavedListing();
    } else {
        trackSaveListing();
    }
};

export const SignInPrompt = ({
    section
}: Pick<UserActionsProps, 'section'>) => {
    const [hasRegistered, setHasRegistered] = useState(false);
    const { toggle, register } = useSavedListing();

    const actionAfterAuth = {
        action: () => {
            register();
            setHasRegistered(true);
        },
        eventLabel: 'ldp_sign_in_save_listing_prompt',
        segment: {
            page: `${section}/details` as BaseTaxonomy['page'],
            section: section as BaseTaxonomy['section'],
            activity: 'listing_details'
        }
    };

    const [isAuthModalShowing, openAuthModal, closeAuthModal] = useModal();
    const [authMode, setAuthMode] = useState<AuthModalMode>('register');

    const handleAuthPrompt = (mode: AuthModalMode) => {
        setAuthMode(mode);
        openAuthModal(null);
    };

    if (hasRegistered) {
        toggle();
        setHasRegistered(false);
    }

    return (
        <>
            <DynamicAuthModal
                isShowing={isAuthModalShowing}
                close={closeAuthModal}
                actionAfterAuth={actionAfterAuth}
                resetActionAfterAuth={() => null}
                heading="Get more from Zoopla"
                initialMode={authMode}
            />
            <DynamicAuthPrompts
                promptType="ldpSaveListing"
                signIn={() => handleAuthPrompt('sign-in')}
                register={() => handleAuthPrompt('register')}
                journey="ldp_sign_in_save_listing_prompt"
            />
        </>
    );
};

export const UserActions = ({
    listingId,
    isStacked,
    openModal,
    mobileOS,
    section
}: UserActionsProps) => {
    const [hasRegistered, setHasRegistered] = useState(false);
    const {
        isReady,
        isSavedOptimistically,
        isUser,
        toggle,
        register
    } = useSavedListing();
    const saveOrigin = useRef(null);
    const shareOrigin = useRef(null);

    const shareClick = () => {
        openModal('sharing', shareOrigin);
        trackOpenSharing(listingId);
    };

    const actionAfterAuth = {
        action: () => {
            register();
            setHasRegistered(true);
        },
        eventLabel: 'LDP_save_listing',
        segment: {
            page: `${section}/details` as BaseTaxonomy['page'],
            section: section as BaseTaxonomy['section'],
            activity: 'listing_details'
        }
    };

    const [isAuthModalShowing, openAuthModal, closeAuthModal] = useModal();

    if (hasRegistered) {
        toggle();
        setHasRegistered(false);
    }

    return (
        <>
            <DynamicAuthModal
                isShowing={isAuthModalShowing}
                close={closeAuthModal}
                actionAfterAuth={actionAfterAuth}
                resetActionAfterAuth={() => null}
            />
            <StyledUserActions isStacked={isStacked}>
                <ButtonShare
                    withBorder={true}
                    isAndroid={mobileOS === 'android'}
                    ref={shareOrigin}
                    onClick={shareClick}
                    data-testid="listing-user-action-share"
                />
                <ButtonSave
                    withBorder={true}
                    isSaved={isSavedOptimistically}
                    ref={saveOrigin}
                    onClick={() => {
                        trackSaveButtonInteraction(
                            listingId,
                            isUser,
                            isSavedOptimistically
                        );
                        toggle(() => openAuthModal(saveOrigin));
                    }}
                    data-testid="listing-user-action-save"
                    data-is-ready={isReady}
                />
            </StyledUserActions>
        </>
    );
};

export const UserActionsGallery = ({
    listingId,
    openModal,
    mobileOS,
    section
}: UserActionsProps) => {
    const [hasRegistered, setHasRegistered] = useState(false);
    const {
        isReady,
        isSavedOptimistically,
        isUser,
        toggle,
        register
    } = useSavedListing();
    const saveOrigin = useRef(null);
    const shareOrigin = useRef(null);

    const shareClick = () => {
        openModal('sharing', shareOrigin);
        trackOpenSharing(listingId);
    };

    const actionAfterAuth = {
        action: () => {
            register();
            setHasRegistered(true);
        },
        eventLabel: 'LDP_save_listing',
        segment: {
            page: `${section}/details` as BaseTaxonomy['page'],
            section: section as BaseTaxonomy['section'],
            activity: 'listing_details'
        }
    };

    const [isAuthModalShowing, openAuthModal, closeAuthModal] = useModal();

    if (hasRegistered) {
        toggle();
        setHasRegistered(false);
    }

    return (
        <>
            <DynamicAuthModal
                isShowing={isAuthModalShowing}
                close={closeAuthModal}
                actionAfterAuth={actionAfterAuth}
                resetActionAfterAuth={() => null}
            />

            <StyledUserActionsGallery>
                <ButtonShare
                    isCollapsible={true}
                    isTranslucent={true}
                    isAndroid={mobileOS === 'android'}
                    onClick={shareClick}
                    ref={shareOrigin}
                    data-testid="listing-user-action-share"
                />
                <ButtonSave
                    isCollapsible={true}
                    isTranslucent={true}
                    isSaved={isSavedOptimistically}
                    ref={saveOrigin}
                    onClick={() => {
                        trackSaveButtonInteraction(
                            listingId,
                            isUser,
                            isSavedOptimistically
                        );
                        toggle(() => openAuthModal(saveOrigin));
                    }}
                    data-testid="listing-user-action-save"
                    data-is-ready={isReady}
                />
            </StyledUserActionsGallery>
        </>
    );
};
